import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Typography,
  Tag,
  Space,
  Select,
  DatePicker,
  Button,
  message,
  Input,
  Row,
  Col,
  Statistic,
  Collapse,
  Modal,
  theme,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { apiLogApi, userApi, authApi } from '../../services/api';
import { APILog, User, APILogStats } from '../../types';
import { usePermissionCheck } from '../../context/PermissionContext';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Panel } = Collapse;

const ApiLogManagement: React.FC = () => {
  const [logs, setLogs] = useState<APILog[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const { canViewAllAPILogs, canViewUsers } = usePermissionCheck();
  const [statsLoading, setStatsLoading] = useState(false);
  const { token } = theme.useToken();
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [stats, setStats] = useState<APILogStats | null>(null);
  
  // 筛选条件
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>();
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | undefined>();
  const [selectedStatusCode, setSelectedStatusCode] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [availableEndpoints, setAvailableEndpoints] = useState<string[]>([]);
  const [availableStatusCodes, setAvailableStatusCodes] = useState<number[]>([]);

  // 详情模态框
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<APILog | null>(null);

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    authApi.getCurrentUser().then(res => {
      setIsAdmin(res.data.is_admin);
    });
  }, []);

  useEffect(() => {
    if (canViewAllAPILogs() || canViewUsers()) {
      fetchUsers();
    }
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLogs(currentPage, pageSize, selectedUserId, selectedEndpoint, selectedStatusCode, dateRange);
  }, [currentPage, pageSize, selectedUserId, selectedEndpoint, selectedStatusCode, dateRange]);

  const fetchUsers = async () => {
    // 只有具有查看所有用户API日志权限的用户才获取用户列表
    if (!canViewAllAPILogs()) {
      return;
    }
    
    try {
      const response = await userApi.getUsers({ page: 1, page_size: 100 });
      setUsers(response.data.users);
    } catch (error) {
      message.error('获取用户列表失败');
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await apiLogApi.getAPILogStats();
      setStats(response.data);
    } catch (error) {
      message.error('获取统计信息失败');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchLogs = async (
    page = currentPage,
    size = pageSize,
    userId = selectedUserId,
    endpoint = selectedEndpoint,
    statusCode = selectedStatusCode,
    range = dateRange
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: size,
        user_id: userId,
        endpoint,
        status_code: statusCode,
        start_time: range && range[0] ? range[0].startOf('day').toISOString() : undefined,
        end_time: range && range[1] ? range[1].endOf('day').toISOString() : undefined,
      };
      const response = await apiLogApi.getAPILogs(params);
      const logsData = response.data.logs;
      setLogs(logsData);
      setTotal(response.data.total);
      // 提取所有唯一的endpoint和状态码
      const endpoints = Array.from(new Set(logsData.map((log) => log.endpoint).filter(Boolean)));
      const statusCodes = Array.from(new Set(logsData.map((log) => log.status_code).filter(Boolean)));
      setAvailableEndpoints(endpoints);
      setAvailableStatusCodes(statusCodes);
    } catch (error) {
      message.error('获取API日志列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchLogs(currentPage, pageSize, selectedUserId, selectedEndpoint, selectedStatusCode, dateRange);
  };

  const handleClearFilters = () => {
    setSelectedUserId(undefined);
    setSelectedEndpoint(undefined);
    setSelectedStatusCode(undefined);
    setDateRange(null);
    setCurrentPage(1);
    fetchLogs(1, pageSize, undefined, undefined, undefined, null);
  };

  const handleViewDetail = (log: APILog) => {
    setSelectedLog(log);
    setDetailModalVisible(true);
  };

  // 状态码标签颜色映射
  const getStatusCodeTagColor = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) return 'green';
    if (statusCode >= 300 && statusCode < 400) return 'blue';
    if (statusCode >= 400 && statusCode < 500) return 'orange';
    if (statusCode >= 500) return 'red';
    return 'default';
  };

  // 格式化显示内容
  const formatContent = (content: string, maxLength: number = 100) => {
    if (!content) return '-';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const columns: ColumnsType<APILog> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 80,
      render: (userId: number) => {
        // 管理员可以看到用户ID和用户名，普通用户只能看到"自己"
        if (!canViewAllAPILogs()) {
          return <div style={{ color: token.colorTextSecondary }}>自己</div>;
        }
        
        const user = users.find(u => u.id === userId);
        return (
          <div>
            <div>{userId}</div>
            {user && <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>{user.name}</div>}
          </div>
        );
      },
    },
    {
      title: 'API端点',
      dataIndex: 'endpoint',
      key: 'endpoint',
      width: 120,
      render: (endpoint: string) => (
        <Tag color="blue">{endpoint}</Tag>
      ),
    },
    {
      title: '状态码',
      dataIndex: 'status_code',
      key: 'status_code',
      width: 100,
      render: (statusCode: number) => (
        <Tag color={getStatusCodeTagColor(statusCode)}>
          {statusCode}
        </Tag>
      ),
    },
    {
      title: 'Token数',
      dataIndex: 'token_count',
      key: 'token_count',
      width: 100,
      render: (tokenCount: number) => tokenCount.toLocaleString(),
    },
    {
      title: '成本',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      render: (cost: number) => `¥${cost.toFixed(4)}`,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
    },
    {
      title: '请求内容',
      dataIndex: 'request',
      key: 'request',
      ellipsis: true,
      width: 200,
      render: (request: string) => formatContent(request),
    },
    {
      title: '响应内容',
      dataIndex: 'response',
      key: 'response',
      ellipsis: true,
      width: 200,
      render: (response: string) => formatContent(response),
    },
    {
      title: '调用时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      width: 120,
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  const canShowUserSelect = isAdmin || canViewUsers();

  // 权限校验：无权限直接不渲染页面
  if (!canViewAllAPILogs() && !canViewUsers()) {
    return null;
  }

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2}>API调用日志</Title>
          <Space>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            >
              清除筛选
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              刷新
            </Button>
          </Space>
        </div>

        {/* 统计信息 */}
        {stats && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic 
                  title="总调用次数" 
                  value={stats.total_calls}
                  loading={statsLoading}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="今日调用" 
                  value={stats.today_calls}
                  loading={statsLoading}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="总成本" 
                  value={stats.total_cost}
                  precision={4}
                  prefix="¥"
                  loading={statsLoading}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="总Token消耗" 
                  value={stats.total_tokens}
                  loading={statsLoading}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* 筛选条件 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {canShowUserSelect && (
              <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>用户筛选</div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="选择用户"
                    value={selectedUserId}
                    onChange={setSelectedUserId}
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const user = users.find(u => u.id === option?.value);
                      if (!user) return false;
                      const searchText = `${user.name} ${user.id}`.toLowerCase();
                      return searchText.includes(input.toLowerCase());
                    }}
                  >
                    {users.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.name} (ID: {user.id})
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
            )}
            
            <Col xs={24} sm={12} md={6}>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>API端点</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="选择API端点"
                  value={selectedEndpoint}
                  onChange={setSelectedEndpoint}
                  allowClear
                >
                  {availableEndpoints.map(endpoint => (
                    <Option key={endpoint} value={endpoint}>
                      {endpoint}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>状态码</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="选择状态码"
                  value={selectedStatusCode}
                  onChange={setSelectedStatusCode}
                  allowClear
                >
                  {availableStatusCodes.map(code => (
                    <Option key={code} value={code}>
                      <Tag color={getStatusCodeTagColor(code)}>{code}</Tag>
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>时间范围</div>
                <RangePicker
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={setDateRange}
                  placeholder={['开始时间', '结束时间']}
                />
              </div>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1400 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 10);
              fetchLogs(page, size || 10, selectedUserId, selectedEndpoint, selectedStatusCode, dateRange);
            },
          }}
        />
      </Card>

      {/* 详情模态框 */}
      <Modal
        title="API调用详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <strong>调用ID:</strong> {selectedLog.id}
              </Col>
              <Col span={12}>
                <strong>用户ID:</strong> {canViewAllAPILogs() ? selectedLog.user_id : '自己'}
                {canViewAllAPILogs() && users.find(u => u.id === selectedLog.user_id) && (
                  <span style={{ marginLeft: 8, color: token.colorTextSecondary }}>
                    ({users.find(u => u.id === selectedLog.user_id)?.name})
                  </span>
                )}
              </Col>
              <Col span={12}>
                <strong>API端点:</strong> <Tag color="blue">{selectedLog.endpoint}</Tag>
              </Col>
              <Col span={12}>
                <strong>状态码:</strong> 
                <Tag color={getStatusCodeTagColor(selectedLog.status_code)}>
                  {selectedLog.status_code}
                </Tag>
              </Col>
              <Col span={12}>
                <strong>Token数:</strong> {selectedLog.token_count.toLocaleString()}
              </Col>
              <Col span={12}>
                <strong>成本:</strong> ¥{selectedLog.cost.toFixed(4)}
              </Col>
              <Col span={12}>
                <strong>IP地址:</strong> {selectedLog.ip}
              </Col>
              <Col span={12}>
                <strong>调用时间:</strong> {new Date(selectedLog.created_at).toLocaleString('zh-CN')}
              </Col>
            </Row>
            
            <div style={{ marginTop: 16 }}>
              <Collapse>
                <Panel header="请求内容" key="request">
                  <pre style={{ 
                    background: token.colorFillAlter, 
                    color: token.colorText,
                    padding: 12, 
                    maxHeight: 300, 
                    overflow: 'auto',
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius
                  }}>
                    {selectedLog.request || '无请求内容'}
                  </pre>
                </Panel>
                <Panel header="响应内容" key="response">
                  <pre style={{ 
                    background: token.colorFillAlter, 
                    color: token.colorText,
                    padding: 12, 
                    maxHeight: 300, 
                    overflow: 'auto',
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius
                  }}>
                    {selectedLog.response || '无响应内容'}
                  </pre>
                </Panel>
                <Panel header="用户代理" key="user_agent">
                  <div style={{ 
                    background: token.colorFillAlter, 
                    color: token.colorText,
                    padding: 12,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius
                  }}>
                    {selectedLog.user_agent || '无用户代理信息'}
                  </div>
                </Panel>
              </Collapse>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApiLogManagement; 